import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { useApi, useSession } from './api/context';
import { Layout, LanguageSwitch } from './components/Layout';
import { ErrorNotice, Loading, ZinqLogo } from './components/ui';
import { useI18n } from './i18n';
import { LiveProvider } from './live/live';
import { CounterQrPage } from './pages/CounterQrPage';
import { InvoicePage } from './pages/InvoicePage';
import { LoginPage } from './pages/LoginPage';
import { OverviewPage } from './pages/OverviewPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { PlanPage } from './pages/PlanPage';
import { ReconciliationPage } from './pages/ReconciliationPage';
import { ResearchPage } from './pages/ResearchPage';
import { MerchantProvider, useMerchantState } from './state/merchant';

/**
 * Routes: /login, /research (the research team's dashboard, its own key, no sign-in) and the
 * signed-in merchant panel under / (overview, payments, reconciliation, invoice, counter QR, plan).
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/research" element={<ResearchPage />} />
      <Route path="/*" element={<SignedIn />} />
    </Routes>
  );
}

function SignedIn() {
  const session = useSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return (
    <MerchantProvider>
      <MerchantGate />
    </MerchantProvider>
  );
}

function MerchantGate() {
  const { state, reload } = useMerchantState();
  if (state.status === 'loading') {
    return (
      <div className="center-screen">
        <Loading />
      </div>
    );
  }
  if (state.status === 'none') return <NoMerchant onRetry={reload} />;
  if (state.status === 'error') {
    return (
      <div className="center-screen">
        <ErrorNotice error={state.error} onRetry={reload} />
      </div>
    );
  }
  return (
    <LiveProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<OverviewPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="reconciliation" element={<ReconciliationPage />} />
          <Route path="invoice" element={<InvoicePage />} />
          <Route path="qr" element={<CounterQrPage />} />
          <Route path="plan" element={<PlanPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </LiveProvider>
  );
}

/** Signed in, but no business profile yet (GET /merchants/me -> 404). */
function NoMerchant({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  const api = useApi();
  return (
    <div className="auth">
      <div className="auth-top">
        <LanguageSwitch />
      </div>
      <main className="auth-card" id="main" data-testid="no-merchant">
        <div className="auth-brand">
          <ZinqLogo size={44} />
          <div>
            <div className="brand-name">ZINQ</div>
            <div className="brand-sub">{t('app.panelName')}</div>
          </div>
        </div>
        <h1>{t('noMerchant.title')}</h1>
        <p>{t('noMerchant.body')}</p>
        <div className="button-row">
          <button type="button" className="btn btn--primary" onClick={onRetry}>
            {t('noMerchant.retry')}
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => void api.logout()}>
            {t('app.logout')}
          </button>
        </div>
      </main>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="card">
      <p>{t('app.notFound')}</p>
      <Link to="/">{t('app.toOverview')}</Link>
    </div>
  );
}
